import { t } from "@lingui/macro";
import { Link, Tag } from "@phosphor-icons/react";
import { URL, urlSchema } from "@reactive-resume/schema";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@reactive-resume/ui";
import { forwardRef, useMemo, type MouseEvent } from "react";

interface Props {
  id?: string;
  value?: URL;
  placeholder?: string;
  labelPlaceholder?: string;
  onChange: (value: URL) => void;
  disableAutoHttps?: boolean;
  swapLabelAndHref?: boolean;
  hideLabelIcon?: boolean;
}

export const URLInput = forwardRef<HTMLInputElement, Props>(
  ({ id, value, placeholder, labelPlaceholder, onChange, disableAutoHttps = false, swapLabelAndHref = false, hideLabelIcon = false, }, ref) => {
    const safeValue = useMemo<URL>(() => {
      if (value && typeof value === "object") {
        return {
          label: "label" in value ? value.label : "",
          href: "href" in value ? value.href : "",
        };
      }

      return { label: "", href: "" };
    }, [value]);

    const hasError = useMemo(() => urlSchema.safeParse(safeValue).success === false, [safeValue]);

    const normalizeToHttps = (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return trimmed;
      if (/^https?:\/\//i.test(trimmed)) return trimmed.replace(/^http:\/\//i, "https://");
      return `https://${trimmed}`;
    };

    // When swapLabelAndHref is true:
    // - Main input edits label (display text); popover edits href (the URL)
    // - Link icon opens the popover; ⌘/Ctrl-click opens href when it looks like a URL
    const mainValue = swapLabelAndHref ? safeValue.label : safeValue.href;
    const popoverValue = swapLabelAndHref ? safeValue.href : safeValue.label;
    // eslint-disable-next-line lingui/no-unlocalized-strings
    const mainPlaceholder = swapLabelAndHref ? (placeholder || "Institution Name") : placeholder;
    const popoverPlaceholder = swapLabelAndHref ? (labelPlaceholder || t`URL`) : (labelPlaceholder || t`Label`);

    const handleMainChange = (newValue: string) => {
      if (swapLabelAndHref) {
        onChange({ ...safeValue, label: newValue });
      } else {
        onChange({ ...safeValue, href: newValue });
      }
    };

    const handleMainBlur = (newValue: string) => {
      if (swapLabelAndHref) {
        onChange({ ...safeValue, label: newValue });
      } else {
        onChange({ ...safeValue, href: disableAutoHttps ? newValue : normalizeToHttps(newValue) });
      }
    };

    const handlePopoverChange = (newValue: string) => {
      if (swapLabelAndHref) {
        onChange({ ...safeValue, href: newValue });
      } else {
        onChange({ ...safeValue, label: newValue });
      }
    };

    const handlePopoverBlur = (newValue: string) => {
      if (swapLabelAndHref) {
        onChange({ ...safeValue, href: disableAutoHttps ? newValue : normalizeToHttps(newValue) });
      } else {
        onChange({ ...safeValue, label: newValue });
      }
    };

    const openHrefIfModifierClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (!swapLabelAndHref || !(event.metaKey || event.ctrlKey)) return;
      const href = safeValue.href?.trim();
      if (!href || !/^https?:\/\//i.test(href)) return;
      event.preventDefault();
      event.stopPropagation();
      window.open(href, "_blank", "noopener,noreferrer");
    };

    return (
      <>
        <div className="flex gap-x-1">
          <Input
            id={id}
            ref={ref}
            value={mainValue}
            className="flex-1"
            hasError={hasError}
            placeholder={mainPlaceholder}
            onChange={(event) => handleMainChange(event.target.value)}
            onBlur={(event) => handleMainBlur(event.target.value)}
          />

          {!hideLabelIcon && <Popover>
            <Tooltip
              content={
                swapLabelAndHref
                  ? 'Edit URL (⌘- or Ctrl-click icon to open)'
                  : 'Label'
              }
            >
              <PopoverTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  type="button"
                  onClick={openHrefIfModifierClick}
                >
                  {swapLabelAndHref ? <Link /> : <Tag />}
                </Button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent className="p-1.5">
              <Input
                value={popoverValue}
                placeholder={popoverPlaceholder}
                onChange={(event) => handlePopoverChange(event.target.value)}
                onBlur={(event) => handlePopoverBlur(event.target.value)}
              />
            </PopoverContent>
          </Popover>}
        </div>

        {hasError && <small className="opacity-75">{t`URL must start with https://`}</small>}
      </>
    );
  },
);
